import { faCalendar, faCircleCheck, faPhone, faTag, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { OrderStatuses, Production, SaleOrder } from 'service';
import ArthaGateway from 'service/ArthaGateway';
import { OrderHeader } from './OrderHeader';

type ConfirmOrderProps = {
    source: SaleOrder;
    user: any;
    onSetActiveTab: (tab: string) => void;
    onOrderUpdated: () => void;
};

export const ConfirmOrder = ({ source, user, onSetActiveTab, onOrderUpdated }: ConfirmOrderProps) => {
    const [productionStage, setProductionStage] = useState<number>(1); // 1 - Pressing, 2 - Packing
    const [order, setOrder] = useState<SaleOrder>();
    const [productionTeams, setProductionTeams] = useState<any[]>([]);
    const brands = ['ISO',
        'Unicorn',
        'Paras',
        'Concept',
        'Talesra',
        'White Tape',
        'New Tape',
        '100% Hardwood Blue',
        '100% Hardwood Red'];

    useEffect(() => {
        if (source) {
            setOrder({ ...source });
            loadProductionPlants();
        }
    }, [source]);

    const loadProductionPlants = () => {
        const cachedTeams = sessionStorage.getItem("productionTeams");
        if (cachedTeams) {
            setProductionTeams(JSON.parse(cachedTeams));
            return;
        }
        ArthaGateway.getProductionPlants()
            .then((data) => {
                setProductionTeams(data);
                sessionStorage.setItem("productionTeams", JSON.stringify(data));
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const confirmOrder = () => {
        if (!user || !order) return;

        if (order.productionPlantId <= 0) {
            toast("Please select a production team.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
            return;
        }

        if (!order.extraInformation || order.extraInformation.trim().length === 0) {
            toast("Please select a brand.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
            return;
        }

        // Validate quantities and prices
        for (let item of order.items) {
            if (item.quantity <= 0) {
                toast("Please enter quantity for all items.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
                return;
            }
            if (item.price <= 0) {
                toast("Please enter price for all items.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
                return;
            }
        }

        order.items.forEach(item => {
            const priceIncludesTax = item.priceIncludesTax;
            const isDiscountPercent = item.isDiscountInPercentage;
            let totalTaxPercentage = (order.supplyStateId != 29)
                ? Number(item.igstTaxPercentage || 0)
                : Number(item.cgstTaxPercentage || 0) + Number(item.sgstTaxPercentage || 0);

            let price = item.price;

            // Calculate gross amount (total before discount/tax)
            let grossAmount = price * item.quantity * (item.squareFeet > 0 ? item.squareFeet : 1);
            let baseForDiscountAndTax;
            if (priceIncludesTax) {
                // Remove tax from gross value as a whole (not per unit!)
                baseForDiscountAndTax = grossAmount * 100 / (100 + totalTaxPercentage);
            } else {
                baseForDiscountAndTax = grossAmount;
            }

            item.grossValue = roundOff(baseForDiscountAndTax);

            // Calculate discount
            let discount;
            if (isDiscountPercent) {
                discount = (item.grossValue * (item.discountPercentage || 0)) / 100;
            } else {
                discount = Number(item.discountPercentage || 0); // in this logic, discountPercentage should be value if not percent
            }
            item.discount = roundOff(discount);
            // Taxable value
            item.taxableValue = roundOff(item.grossValue - item.discount);

            // Calculate tax amounts
            let cgstTax = 0, sgstTax = 0, igstTax = 0;
            if (order.supplyStateId === 29) {
                cgstTax = (item.taxableValue * (item.cgstTaxPercentage || 0)) / 100;
                sgstTax = (item.taxableValue * (item.sgstTaxPercentage || 0)) / 100;
                cgstTax = roundOff(cgstTax);
                sgstTax = roundOff(sgstTax);
            } else {
                igstTax = (item.taxableValue * (item.igstTaxPercentage || 0)) / 100;
                igstTax = roundOff(igstTax);
            }
            item.cgstTax = cgstTax;
            item.sgstTax = sgstTax;
            item.igstTax = igstTax;

            // Net value (final bill value for item)
            item.netValue = roundOff(item.taxableValue + item.cgstTax + item.sgstTax + item.igstTax);
        });

        ArthaGateway.confirmSalesOrder(order, user.id)
            .then((data) => {
                toast("Order confirmed successfully.", { position: 'bottom-center', className: 'btn bg-success text-white' });
                onOrderUpdated();
            })
            .catch((error) => {
                console.log(error);
                toast("Failed to confirm order.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
            });
    }

    const roundOff = (num, decimals = 2) => {
        const factor = Math.pow(10, decimals);
        return Math.round(num * factor) / factor;
    }

    const updateQuantity = (index: number, quantity: number) => {
        if (!order) return;

        setOrder(prevOrder => {
            if (!prevOrder) return undefined;
            return {
                ...prevOrder,
                items: prevOrder.items.map((item, idx) =>
                    idx === index ? { ...item, quantity: quantity } : item
                )
            };
        });
    }

    const updatePrice = (index: number, price: number) => {
        if (!order) return;

        setOrder(prevOrder => {
            if (!prevOrder) return undefined;
            return {
                ...prevOrder,
                items: prevOrder.items.map((item, idx) =>
                    idx === index ? { ...item, price: price } : item
                )
            };
        });
    }

    return (
        <>
            {order && (
                <div className='card'>
                    <div className="card-header">
                        <h3 className="order-id">Order ID: <b>{order.voucherNumber}</b></h3>
                    </div>
                    <OrderHeader order={order} user={user} />
                    <div className='card-body p-2'>
                        <label>Production Team</label>
                        <select className="form-select" value={order.productionPlantId} onChange={(e) => setOrder({ ...order, productionPlantId: Number(e.target.value) })}>
                            {productionTeams.map(team => (
                                <option key={team.id} value={team.id}>{team.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className='card-body p-2'>
                        <label>Brand</label>
                        <select className="form-select" value={order.extraInformation} onChange={(e) => setOrder({ ...order, extraInformation: e.target.value })}>
                            {brands.map(brand => (
                                <option key={brand} value={brand}>{brand}</option>
                            ))}
                        </select>
                    </div>
                    <div className='card-body p-0'>
                        <table className="table table-bordered bg-white mb-2">
                            <thead>
                                <tr className='text-center'>
                                    <th className="w-50">Quantity</th>
                                    <th>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.items.map((item, index) => (
                                    <>
                                        <tr key={index}>
                                            <td colSpan={2}>
                                                <b>{item.product.name}</b>
                                                {item.style?.name && <span> (Style: {item.style.name})</span>}
                                                {item.notes && <span> ({item.notes || '-'})</span>}
                                            </td>
                                        </tr>
                                        <tr key={index + "-qty"}>
                                            <td className="p-0">
                                                <input type="number" className="form-control" value={item.quantity} onChange={(e) => updateQuantity(index, Number(e.target.value))} />
                                            </td>
                                            <td className="p-0">
                                                <input type="number" className="form-control" value={item.price} onChange={(e) => updatePrice(index, Number(e.target.value))} />
                                            </td>
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="card-footer p-2">
                        <div className="btn btn-success w-100 mb-2" onClick={confirmOrder}>
                            Confirm Order
                        </div>
                        <div className="btn btn-sm btn-danger w-100" onClick={() => { onSetActiveTab('details'); }}>
                            Cancel
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}